import * as React from 'react';
import { Component } from 'react';
import { action, computed, toJS } from 'mobx';
import { observer, inject } from 'mobx-react';
import styled, { withTheme } from 'styled-components';
import { propStyles } from '@kurtosys/ksys-portal-components/build/utils/propStyles';
import * as Stickyfill from 'stickyfilljs';
import get from 'lodash/get';
import Icon from './Icon';
import DownloadControls from './Document/DownloadControls';
import { DataStore } from '../stores';
import { ShareClass } from '../models/entity';
import Translate from './Translate';
import { IQueryContext } from '@kurtosys/ksys-app-template/dist/models/query';
import getQueryResults from '../utils/query/getQueryResults';
import {
    FundGroupHeaderElement as IFundGroupHeaderElement,
    FundGroupHeaderWatchlist,
} from '../models/configuration/FundGroupHeader';
import HTag from './HTag';
import WatchListToggle from './WatchListToggle';
import { camelCase } from 'change-case';
import { SubGroupedFund } from '../models/groupedFunds';
import { FundGroupHeader as FundGroupHeaderModel } from '../models/configuration/FundGroupHeader';
import { KEYBOARD_KEYS } from '../constants';

const ACCEPT_KEYS = [
    KEYBOARD_KEYS.ENTER,
    KEYBOARD_KEYS.SPACE,
    KEYBOARD_KEYS.SPACEBAR,
];

const FundHeader = styled<any, any>('div')`
    ${propStyles};
    top: ${p => `${p.stickyOffset || 0}px`};
    z-index: 1;
`;

const FundGroupHeaderWatchlist = styled<any, any>('div')`
    ${propStyles}
`;

const Controls = styled<any, any>('div')`
    ${propStyles}
    display: flex;
    flex-direction: row;
`;

FundHeader.defaultProps = {
    position: ['sticky', 'static'],
    display: 'flex',
    alignItems: 'center',
    fontFamily: 'heading',
    fontWeight: 'medium',
    fontSize: 'md',
    paddingX: 'md',
    paddingY: 'md',
    color: 'white',
    bgColor: 'secondary',
    borderWidth: '1px',
    borderColor: 'white',
    borderStyle: 'none none solid none',
};

const AdditionalElement = styled<any, any>('div')`
    ${propStyles};
`;

AdditionalElement.defaultProps = {
    marginX: 'sm',
};

const LabelValue = styled<any, any>('span')`
    ${propStyles};
`;

const iconRightStyleProps = {
    order: 1,
    margin: '0 0 0 auto',
};

interface FundGroupHeaderProps {
    theme?: any;
    dataStore?: DataStore;
    shareClasses: Array<ShareClass>;
    fundName: string;
    parentFundName?: string;
    rowType: string;
    expanded: boolean;
    toggleExpanded: () => void;
    downloadCount: number;
    documentDownloads: Array<string>;
    isSubFund: boolean;
    metricContext?: string;
    subGroupedFund?: SubGroupedFund;
}

@inject('dataStore')
@observer
class FundGroupHeader extends Component<FundGroupHeaderProps> {
    setStickyfill = element => {
        if (element) {
            Stickyfill.addOne(element);
        }
    };

    @computed
    get stickyOffset() {
        return this.props.dataStore.config.fundGroupHeader.stickyOffset;
    }

    @computed
    get expander() {
        return this.props.dataStore.config.fundGroupHeader.expander || {};
    }

    @computed
    get tile() {
        return this.props.dataStore.config.fundGroupHeader.tile;
    }

    @computed
    get additionalElementsQueries(): Array<IFundGroupHeaderElement> {
        const { isSubFund, dataStore } = this.props;
        const {
            additionalElements,
            subHeaderAdditionalElements,
        } = dataStore.config.fundGroupHeader;
        return isSubFund ? subHeaderAdditionalElements : additionalElements;
    }

    @computed
    get headerExpandIconFilename() {
        const {
            expandIconFileName = 'HeaderChevronIconExpand',
            collapseIconFileName = 'HeaderChevronIconCollapse',
        } = this.expander;

        return this.props.expanded ? collapseIconFileName : expandIconFileName;
    }

    @computed
    get headerExpandable() {
        const {
            expandable = true,
        } = this.props.dataStore.config.fundGroupHeader;

        return expandable;
    }

    @computed
    get showExpanderIcon() {
        const { showIcon = true } = this.expander;

        return showIcon;
    }

    @computed
    get tileIconFilename() {
        const [firstShareClass] = this.props.shareClasses;
        const { property, propertyFilenameMap } = this.tile;
        const propertyValue = get(
            firstShareClass,
            `properties_pub.${property}.value`,
            ''
        );
        return propertyFilenameMap[propertyValue] || '';
    }

    @computed
    get hasDocumentDownloads() {
        return this.props.rowType === 'Documents';
    }

    @computed
    get isSubFund(): boolean {
        return !!this.props.isSubFund;
    }

    @computed
    get watchlistEnabled(): boolean {
        const groupWatchlist = this.groupWatchlist;
        return !!(this.isSubFund
            ? this.props.subGroupedFund &&
              this.props.subGroupedFund.valid &&
              groupWatchlist.enabled
            : groupWatchlist.enabled);
    }

    @computed
    get groupWatchlist(): FundGroupHeaderWatchlist {
        const { watchlist } = this.fundGroupHeader;
        if (watchlist) {
            return (
                (this.isSubFund
                    ? watchlist.fundSubGroup
                    : watchlist.fundGroup) || {}
            );
        }
        return {};
    }

    @computed
    get fundHeader() {
        const { theme, isSubFund } = this.props;
        return {
            ...(isSubFund ? theme.SubFundHeader : theme.FundHeader),
        };
    }

    @computed
    get fundHeaderAdditionalElementTheme() {
        const { theme, isSubFund } = this.props;
        return {
            // backwards compatibility for theme.FundHeaderFundName
            ...theme.FundHeaderFundName,
            ...(isSubFund
                ? theme.SubFundHeaderAdditionalElement
                : theme.FundHeaderAdditionalElement),
        };
    }

    @computed
    get fundHeaderAdditionalElementLabelTheme() {
        const { theme, isSubFund } = this.props;
        return {
            // backwards compatibility for theme.FundHeaderLabel
            ...theme.FundHeaderLabel,
            ...(isSubFund
                ? theme.SubFundHeaderAdditionalElementLabel
                : theme.FundHeaderAdditionalElementLabel),
        };
    }

    @computed
    get fundHeaderAdditionalElementValueTheme() {
        const { theme, isSubFund } = this.props;
        return {
            // backwards compatibility for theme.FundHeaderValue
            ...theme.FundHeaderValue,
            ...(isSubFund
                ? theme.SubFundHeaderAdditionalElementValue
                : theme.FundHeaderAdditionalElementValue),
        };
    }

    @computed
    get fundHeaderExpandIconTheme() {
        const { theme, isSubFund } = this.props;
        let fundHeaderExpandIcon = theme.FundHeaderExpandIcon;
        if (isSubFund) {
            fundHeaderExpandIcon = {
                ...fundHeaderExpandIcon,
                ...theme.SubFundHeaderExpandIcon,
            };
        }
        return fundHeaderExpandIcon;
    }

    @computed
    get fundHeaderTileIconTheme() {
        const { theme, isSubFund } = this.props;
        let fundHeaderTileIcon = theme.FundHeaderTileIcon;
        if (isSubFund) {
            fundHeaderTileIcon = {
                ...fundHeaderTileIcon,
                ...theme.SubFundHeaderTileIcon,
            };
        }
        return fundHeaderTileIcon;
    }

    @computed
    get fundHeaderDownloadControlsTheme() {
        const { theme, isSubFund } = this.props;
        let fundHeaderDownloadControls = theme.FundHeaderDownloadControls;
        if (isSubFund) {
            fundHeaderDownloadControls = {
                ...fundHeaderDownloadControls,
                ...theme.SubFundHeaderDownloadControls,
            };
        }
        return fundHeaderDownloadControls;
    }

    @computed
    get fundHeaderDownloadButtonTheme() {
        const { theme, isSubFund } = this.props;
        let fundHeaderDownloadButton = theme.FundHeaderDownloadButton;
        if (isSubFund) {
            fundHeaderDownloadButton = {
                ...fundHeaderDownloadButton,
                ...theme.SubFundHeaderDownloadButton,
            };
        }
        return fundHeaderDownloadButton;
    }

    @computed
    get fundHeaderDownloadCountTheme() {
        const { theme, isSubFund } = this.props;
        let fundHeaderDownloadCount = theme.FundHeaderDownloadCount;
        if (isSubFund) {
            fundHeaderDownloadCount = {
                ...fundHeaderDownloadCount,
                ...theme.SubFundHeaderDownloadCount,
            };
        }
        return fundHeaderDownloadCount;
    }

    @computed
    get additionalElements() {
        return (
            this.additionalElementsQueries &&
            this.additionalElementsQueries.map((elementQuery, idx) => {
                const { label, value } = this.additionalElementsQueryResults(
                    elementQuery
                );
                const ariaId = `fund-${this.headerTextId}-group-header-add-ele-${idx}`;
                return (
                    value && (
                        <AdditionalElement
                            key={`${label}${idx}`}
                            {...this.fundHeaderAdditionalElementTheme}
                        >
                            <LabelValue
                                {...this.fundHeaderAdditionalElementLabelTheme}
                                id={ariaId}
                            >
                                <Translate>{label}</Translate>
                            </LabelValue>
                            <LabelValue
                                {...this.fundHeaderAdditionalElementValueTheme}
                                aria-labelledby={ariaId}
                            >
                                <Translate>{value}</Translate>
                            </LabelValue>
                        </AdditionalElement>
                    )
                );
            })
        );
    }

    additionalElementsQueryResults(query) {
        const { dataStore } = this.props;
        const { culture } = dataStore;
        const { entityType } = dataStore.config;
        const [firstShareClass] = this.props.shareClasses;
        const queryContext: IQueryContext = {
            entityByType: {
                [entityType]: firstShareClass as any,
            },
        };
        return getQueryResults(
            queryContext,
            query,
            culture,
            dataStore.appDataProps
        );
    }

    @computed
    get headerText() {
        const { dataStore, fundName } = this.props;
        let headerText = fundName;
        const fundGroupHeader = dataStore.config.fundGroupHeader;
        if (fundGroupHeader) {
            const {
                fundGroupPropertyValueAliases: valueAliases,
                fundGroupPropertyGlobalValueAliases: globalValueAliases,
            } = fundGroupHeader;
            if (valueAliases || globalValueAliases) {
                const aliases = dataStore.getAliases(
                    valueAliases,
                    globalValueAliases
                );
                headerText = get(aliases, fundName, fundName);
            }
        }
        return headerText;
    }

    @computed
    get headerTextThemeName() {
        const { isSubFund } = this.props;
        return isSubFund ? 'SubFundHeaderFundName' : 'FundHeaderFundName';
    }

    @computed
    get headerTextId() {
        return this.headerText.toLowerCase().replace(/\W/g, '-');
    }

    @computed
    get metricId() {
        const { fundName, parentFundName, isSubFund } = this.props;
        return isSubFund
            ? // tslint:disable-next-line:prettier
                `fundHeader-${camelCase(parentFundName)}-fundSubHeader-${camelCase(fundName)}`
            : `fundHeader-${camelCase(fundName)}`;
    }

    @computed
    get property(): string {
        return this.isSubFund
            ? this.fundGroupHeader.fundSubGroupProperty
            : this.fundGroupHeader.fundGroupProperty;
    }

    @computed
    get parentProperty(): string {
        return this.fundGroupHeader.fundGroupProperty;
    }

    @computed
    get clientCodeProperty(): string {
        const { dataStore } = this.props;
        return (
            (dataStore &&
                dataStore.config &&
                dataStore.config.explorer &&
                dataStore.config.explorer.watchlist &&
                dataStore.config.explorer.watchlist.property) ||
            'isin'
        );
    }

    @computed
    get fundGroupHeader(): FundGroupHeaderModel {
        const { dataStore } = this.props;
        const fundGroupHeader =
            (dataStore &&
                dataStore.config &&
                dataStore.config.fundGroupHeader) ||
            {};
        return fundGroupHeader;
    }

    @computed
    get childEntities() {
        const { dataStore, fundName, subGroupedFund } = this.props;
        const { watchlistHierarchy } = dataStore;
        let childEntities = [];
        const value = fundName;
        const parentValue = subGroupedFund && subGroupedFund.parent;
        if (this.isSubFund) {
            childEntities =
                (watchlistHierarchy[parentValue] &&
                    watchlistHierarchy[parentValue][value]) ||
                [];
        } else {
            childEntities =
                watchlistHierarchy[value]?._all ||
                (Array.isArray(watchlistHierarchy[value]) &&
                    watchlistHierarchy[value]) ||
                [];
        }
        return childEntities;
    }

    @computed
    get inWatchList() {
        const { dataStore, subGroupedFund } = this.props;
        if (!subGroupedFund || subGroupedFund.valid) {
            const { watchList, watchlistHierarchy } = dataStore;
            if (!watchlistHierarchy) {
                return false;
            }
            const childEntities = this.childEntities;
            let allPresent = false;
            if (childEntities.length > 0) {
                allPresent = true;
                for (const entity of childEntities) {
                    const watchlistPropertyValue = get(
                        entity,
                        `properties_pub.${dataStore.config.watchListProperty}.value`,
                        ''
                    );
                    if (!watchList.includes(watchlistPropertyValue)) {
                        allPresent = false;
                        break;
                    }
                }
            }
            return allPresent;
        }
        return false;
    }

    @action
    toggleWatchList = () => {
        if (this.inWatchList) {
            this.removeWatchList();
        } else {
            this.addWatchList();
        }
    };

    @action
    addWatchList = () => {
        const { dataStore } = this.props;
        const updatedWatchList = toJS(this.props.dataStore.watchList);
        const childEntities = this.childEntities;
        if (childEntities && childEntities.length > 0) {
            updatedWatchList.push(
                ...childEntities
                    .map(childEntity => {
                        return get(
                            childEntity,
                            `properties_pub.${dataStore.config.watchListProperty}.value`,
                            ''
                        );
                    })
                    .filter(watchlistValue => {
                        return !updatedWatchList.includes(watchlistValue);
                    })
            );
            this.props.dataStore.watchList = updatedWatchList;
        }
    };

    @action
    removeWatchList = () => {
        const { dataStore } = this.props;
        const updatedWatchList = toJS(this.props.dataStore.watchList);
        const childEntities = this.childEntities;
        if (childEntities && childEntities.length > 0) {
            const childWatchlistValues = childEntities.map(childEntity => {
                return get(
                    childEntity,
                    `properties_pub.${dataStore.config.watchListProperty}.value`,
                    ''
                );
            });
            this.props.dataStore.watchList = updatedWatchList.filter(
                value => !childWatchlistValues.includes(value)
            );
        }
    };

    @computed
    get watchListToggleTheme() {
        const { theme } = this.props;
        return this.isSubFund
            ? theme.SubFundHeaderWatchListToggle
            : theme.FundHeaderWatchListToggle;
    }

    @computed
    get watchlistIcons() {
        return this.groupWatchlist.icons || {};
    }

    expandIconOnKeyDown = (e: any) => {
        const key = e.key;
        if (ACCEPT_KEYS.indexOf(key as KEYBOARD_KEYS) !== -1) {
            e.preventDefault();
            if (this.headerExpandable) {
                const { toggleExpanded } = this.props;
                if (toggleExpanded) {
                    toggleExpanded();
                }
            }
        }
    };

    getFundGroupPropertyId(): string | number | Array<string | number> {
        const { dataStore, shareClasses } = this.props;
        const { fundGroupPropertyId, fundGroupProperty } = dataStore.config;
        const fundIdentifier = fundGroupPropertyId || fundGroupProperty;
        const fundIdentifierValue =
            shareClasses[0]?.properties_pub[fundIdentifier]?.value;
        return !fundGroupPropertyId && typeof fundIdentifierValue === 'string'
            ? camelCase(fundIdentifierValue)
            : fundIdentifierValue;
    }

    render() {
        const {
            expanded,
            theme,
            dataStore,
            fundName,
            toggleExpanded,
            downloadCount,
            documentDownloads,
        } = this.props;

        return (
            <FundHeader
                stickyOffset={this.stickyOffset}
                ref={this.setStickyfill}
                onClick={this.headerExpandable ? toggleExpanded : undefined}
                cursor={this.headerExpandable ? 'pointer' : 'auto'}
                data-metric-id={this.metricId}
                {...this.fundHeader}
            >
                <Controls
                    {...(this.expander.align === 'right' &&
                        iconRightStyleProps)}
                >
                    {this.watchlistEnabled && (
                        <FundGroupHeaderWatchlist>
                            <WatchListToggle
                                onClick={e => {
                                    e.stopPropagation();
                                    this.toggleWatchList();
                                }}
                                active={this.inWatchList}
                                accessibilityContext={`for fund group of ${fundName}`}
                                metricContext={camelCase(this.metricId)}
                                iconOnFileName={this.watchlistIcons.on}
                                iconOffFileName={this.watchlistIcons.off}
                                {...this.watchListToggleTheme}
                            />
                        </FundGroupHeaderWatchlist>
                    )}
                    {this.headerExpandable && this.showExpanderIcon && (
                        <Icon
                            role="button"
                            tabIndex="0"
                            onKeyDown={this.expandIconOnKeyDown}
                            aria-expanded={expanded}
                            fileName={this.headerExpandIconFilename}
                            iconSize="md"
                            {...this.fundHeaderExpandIconTheme}
                            accessibilityContext={`for fund group of ${fundName}`}
                        />
                    )}
                </Controls>

                {this.tile && (
                    <Icon
                        fileName={this.tileIconFilename}
                        {...(this.tile.align === 'right' &&
                            iconRightStyleProps)}
                        iconSize="md"
                        {...this.fundHeaderTileIconTheme}
                    />
                )}
                <HTag
                    componentTheme={this.headerTextThemeName}
                    defaultStyleProps={{
                        marginX: 'sm',
                        marginY: '0',
                        fontFamily: 'heading',
                        fontWeight: 'medium',
                        fontSize: 'md',
                    }}
                    level={1}
                    text={this.headerText}
                />
                {this.additionalElements}
                {this.hasDocumentDownloads && (
                    <DownloadControls
                        label="Download"
                        margin="0 0 0 auto"
                        showPreview={
                            dataStore.config.documentPreviewEnabled &&
                            !dataStore.isZipDocument(documentDownloads)
                        }
                        count={downloadCount}
                        disabled={downloadCount === 0}
                        onClick={e => {
                            e.stopPropagation();
                            dataStore.downloadDocuments(documentDownloads);
                        }}
                        onPreviewClick={e => {
                            e.stopPropagation();
                            dataStore.previewDocuments(documentDownloads);
                        }}
                        href={dataStore.getDocumentLink(documentDownloads)}
                        downloadButtonFileName="HeaderDownloadIcon"
                        previewButtonFileName={
                            dataStore.config.documentPreviewIcon
                        }
                        theme={{
                            ...theme,
                            ...{
                                DownloadControls: this
                                    .fundHeaderDownloadControlsTheme,
                                DownloadButton: {
                                    fontSize: 'sm',
                                    ...this.fundHeaderDownloadButtonTheme,
                                },
                                DownloadCount: this
                                    .fundHeaderDownloadCountTheme,
                            },
                        }}
                        fileName={dataStore.getFileName(documentDownloads)}
                        metricContext={this.metricId}
                        clientCode={this.getFundGroupPropertyId()}
                    />
                )}
            </FundHeader>
        );
    }
}

export default withTheme(FundGroupHeader);



// WEBPACK FOOTER //
// ./src/Components/FundGroupHeader.tsx